import { Routes } from '@angular/router';
import { AppLayoutComponent } from '@shared/layout/app-layout/app-layout.component';
import { authGuard } from '@shared/auth/guards/auth.guard';
import { redirectAuthenticatedGuard } from '@shared/auth/guards/redirect-authenticated.guard';
import { adminGuard } from '@shared/auth/guards/admin.guard';
import { permissionGuard, requireActionGuard } from '@shared/auth/guards/permission.guard';
import { transferenciaTokenGuard } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-token.guard';
import { transferenciaConfirmarResolver } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.resolver';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'inicio',
        loadComponent: () =>
          import('@partnercore/feature-home/inicio/inicio.component').then(
            (m) => m.InicioComponent,
          ),
        title: 'Inicio - Sistema Constructora',
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('@smartpacking/feature-configuration-master/configuration-master.routes').then(
            (m) => m.CONFIGURATION_MASTER_ROUTES,
          ),
      },
      {
        path: 'security',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('@smartpacking/equipment-api-security/equipment-api-security.routes').then(
            (m) => m.EQUIPMENT_API_SECURITY_ROUTES,
          ),
      },
      {
        path: 'full-traceability',
        loadChildren: () =>
          import('@smartpacking/feature-full-traceability/full-traceability.routes').then(
            (m) => m.FULL_TRACEABILITY_ROUTES,
          ),
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('@partnercore/feature-modules/modulos/control-general/notificaciones/notifications/notifications.component').then(
            (m) => m.NotificationsComponent,
          ),
        title: 'Notificaciones - Sistema Constructora',
      },
      {
        path: 'configuracion',
        loadComponent: () =>
          import('@partnercore/feature-settings/configuracion/configuracion.component').then(
            (m) => m.ConfiguracionComponent,
          ),
        title: 'Configuración - Sistema Constructora',
      },
      {
        path: 'configuracion/transferir',
        loadComponent: () =>
          import('@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.component').then(
            (m) => m.TransferenciaConfirmarComponent,
          ),
        title: 'Confirmar Transferencia de Propiedad - Sistema Constructora',
        canActivate: [transferenciaTokenGuard],
        resolve: { transferenciaInfo: transferenciaConfirmarResolver },
      },
      {
        path: 'packaging',
        loadChildren: () =>
          import('libs/smartpacking/feature-packaging/src/packaging.routes').then(
            (m) => m.packagingRoutes,
          ),
      },
      // Dashboard Sistema
      {
        path: 'dashboard-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard-general',
            pathMatch: 'full',
          },
          {
            path: 'dashboard-general',
            loadComponent: () =>
              import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(
                (m) => m.DashboardGeneralComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Dashboard - General',
          },
        ],
      },
      // Módulos Admin Sistema
      {
        path: 'admin-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () =>
              import('@partnercore/feature-admin/users/users.component').then(
                (m) => m.UsersComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Usuarios Sistema',
          },
          {
            path: 'permisos',
            loadComponent: () =>
              import('@partnercore/feature-admin/permisos').then((m) => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'Permisos Sistema',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () =>
              import('@partnercore/feature-admin/permisos-editar').then(
                (m) => m.PermisosEditarComponent,
              ),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'Permisos Sistema',
          },
          {
            path: 'roles',
            loadComponent: () =>
              import('@partnercore/feature-admin/roles/roles.component').then(
                (m) => m.RolesComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Roles Sistema',
          },
          {
            path: 'perfil',
            loadComponent: () =>
              import('@partnercore/feature-profile/perfil/perfil.component').then(
                (m) => m.PerfilComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Perfil Sistema',
          },
          {
            path: 'empresas',
            loadComponent: () =>
              import('@partnercore/feature-admin/empresas/empresas.component').then(
                (m) => m.EmpresasComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Empresas Sistema',
          },
          {
            path: 'empresas/:id',
            loadComponent: () =>
              import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(
                (m) => m.EmpresaDetailComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Detalle de Empresa',
          },
          {
            path: 'clientes',
            loadComponent: () =>
              import('@partnercore/feature-admin/clientes/clientes.component').then(
                (m) => m.ClientesComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Clientes Sistema',
          },
        ],
      },
      // Seguridad Sistema (SYS) — ruta top-level para que coincida con la URL sembrada en DB: /seguridad-sistema/*
      {
        path: 'seguridad-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () =>
              import('@partnercore/feature-sessions/sesiones/sesiones.component').then(
                (m) => m.SesionesComponent,
              ),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'Gestión de Sesiones - Panel Sistema',
          },
          {
            path: 'auditorias',
            loadComponent: () =>
              import('@partnercore/feature-audit/auditoria/auditoria.component').then(
                (m) => m.AuditoriaComponent,
              ),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'Auditorías - Panel Sistema',
          },
        ],
      },
      // Dashboard Control General
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: 'general',
            loadComponent: () =>
              import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(
                (m) => m.DashboardGeneralComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Dashboard - General',
          },
        ],
      },
      // Módulos Administrativos
      {
        path: 'admin',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () =>
              import('@partnercore/feature-admin/users/users.component').then(
                (m) => m.UsersComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Usuarios',
          },
          {
            path: 'permisos',
            loadComponent: () =>
              import('@partnercore/feature-admin/permisos').then((m) => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'Permisos',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () =>
              import('@partnercore/feature-admin/permisos-editar').then(
                (m) => m.PermisosEditarComponent,
              ),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'Permisos',
          },
          {
            path: 'roles',
            loadComponent: () =>
              import('@partnercore/feature-admin/roles/roles.component').then(
                (m) => m.RolesComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Roles Administrativos',
          },
          {
            path: 'perfil',
            loadComponent: () =>
              import('@partnercore/feature-profile/perfil/perfil.component').then(
                (m) => m.PerfilComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Perfil',
          },
          {
            path: 'empresas',
            loadComponent: () =>
              import('@partnercore/feature-admin/empresas/empresas.component').then(
                (m) => m.EmpresasComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Empresas',
          },
          {
            path: 'empresas/:id',
            loadComponent: () =>
              import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(
                (m) => m.EmpresaDetailComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Detalle de Empresa',
          },
        ],
      },
      // Estructura
      {
        path: 'estructura-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'modulos-sistema',
            pathMatch: 'full',
          },
          {
            path: 'modulos-sistema',
            loadComponent: () =>
              import('@partnercore/feature-modules/modulos/module.component').then(
                (m) => m.ModuleComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Módulos Sistema',
          },
        ],
      },
      // Maestros Sistema
      {
        path: 'maestros-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () =>
              import('@partnercore/feature-currencies/currencies/currencies.component').then(
                (m) => m.CurrenciesComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Monedas Sistema',
          },
        ],
      },
      /** ELIMINAR ESTA RUTA DESPUES DE MOSTRAR EJEMPLO */
      {
        path: 'test',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'generic',
            loadComponent: () =>
              import('../app/temp/generic/generic.component').then((m) => m.GenericComponent),
            canActivate: [permissionGuard],
            title: 'Monedas Sistema',
          },
        ],
      },
      {
        path: 'reception',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('@smartpacking/feature-receptionguide/receptionguide.routes').then(
            (m) => m.reception_guide_routes
          ),
      },
      // Maestros
      {
        path: 'maestros',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () =>
              import('@partnercore/feature-currencies/currencies/currencies.component').then(
                (m) => m.CurrenciesComponent,
              ),
            canActivate: [permissionGuard],
            title: 'Monedas',
          },
        ],
      },
      // Seguridad Control General — ruta top-level para que coincida con la URL sembrada en DB: /seguridad/*
      {
        path: 'seguridad',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () =>
              import('@partnercore/feature-sessions/sesiones/sesiones.component').then(
                (m) => m.SesionesComponent,
              ),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'Gestión de Sesiones - Panel de Control',
          },
          {
            path: 'auditorias',
            loadComponent: () =>
              import('@partnercore/feature-audit/auditoria/auditoria.component').then(
                (m) => m.AuditoriaComponent,
              ),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'Auditorías - Panel de Auditoría',
          },
        ],
      },
      // Planificación de producción
      {
        path: 'planning',
        canActivate: [permissionGuard],
        children: [
          {
            path: 'reception',
            loadChildren: () =>
              import('@smartpacking/reception-programming/module.routes').then(
                (m) => m.RECEPTION_PROGRAMMING_ROUTES,
              ),
            canActivate: [permissionGuard],
            title: 'Programación de recepción',
          },
          {
            path: 'process',
            loadChildren: () =>
              import('@smartpacking/process-line-planning/module.routes').then(
                (m) => m.PROCESS_LINE_PLANNING_ROUTES,
              ),
            canActivate: [permissionGuard],
            title: 'Planificación de líneas de proceso',
          },
          {
            path: 'production-orders',
            loadChildren: () =>
              import('@smartpacking/production-order/module.routes').then(
                (m) => m.PRODUCTION_ORDER_ROUTES,
              ),
            canActivate: [permissionGuard],
            title: 'Gestión de órdenes de producción',
          },
          {
            path: 'shifts',
            loadChildren: () =>
              import('@smartpacking/work-shift/module.routes').then(
                (m) => m.WORK_SHIFT_ROUTES,
              ),
            canActivate: [permissionGuard],
            title: 'Gestión de turnos y personal',
          },
          {
            path: 'packaging',
            loadChildren: () =>
              import('@smartpacking/packaging-material-planning/module.routes').then(
                (m) => m.PACKAGING_MATERIAL_PLANNING_ROUTES,
              ),
            canActivate: [permissionGuard],
            title: 'Planificación de empaque y materiales',
          },
        ],
      },
      /** PACKING */
      {
        path: 'quality',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('libs/smartpacking/feature-quality/src/quality.routes')
            .then(m => m.quality_routes),
      },
      {
        path: 'cold-chain',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('@smartpacking/feature-cold-chain/cold-chain.routes')
            .then(m => m.coldChainRoutes),
      },
      {
        path: 'logistics',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('@smartpacking/feature-export-logistics/export-logistics.routes')
            .then(m => m.exportLogisticsRoutes),
      },
      {
        path: 'classification',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('@smartpacking/feature-product-classification/classification.routes')
            .then(m => m.classificationRoutes),
      },
      /** RRHH — el path debe coincidir con `/hr/*` de `DataInitializer.initializeModules()` */
      {
        path: 'hr',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('@smartpacking/feature-rrhh/rrhh.routes')
            .then(m => m.rrhh_routes),
      },
      /** Gestión de Insumos y Proveedores */
      {
        path: 'supplies',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import(
            '@smartpacking/feature-gestion-insumos-proveedores/gestion-insumos-proveedores.routes'
          ).then((m) => m.gestionInsumosProveedoresRoutes),
      },
      {
        path: 'maintenance',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import('@smartpacking/feature-maintenance/maintenance.routes').then(
            (m) => m.MAINTENANCE_ROUTES,
          ),
      },
      {
        path: 'gestion-insumos-proveedores',
        canActivate: [permissionGuard],
        loadChildren: () =>
          import(
            '@smartpacking/feature-gestion-insumos-proveedores/gestion-insumos-proveedores.routes'
          ).then((m) => m.gestionInsumosProveedoresRoutes),
      },
    ],
  },
  // Auth
  {
    path: 'auth',
    loadComponent: () =>
      import('@partnercore/feature-auth-pages/auth/auth.component').then((m) => m.AuthComponent),
    canActivate: [redirectAuthenticatedGuard],
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('@partnercore/feature-auth-pages/auth-pages/login/login.component-page').then(
            (m) => m.LoginPageComponent,
          ),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('@partnercore/feature-auth-pages/auth-pages/forgot-password/forgot-password-page.component').then(
            (m) => m.ForgotPasswordPageComponent,
          ),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('@partnercore/feature-auth-pages/auth-pages/reset-password/reset-password-page.component').then(
            (m) => m.ResetPasswordPageComponent,
          ),
      },
    ],
  },
  // Error
  {
    path: '404',
    loadComponent: () =>
      import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(
        (m) => m.NotFoundComponent,
      ),
    title: 'Página no encontrada - Sistema Constructora',
  },
  {
    path: '**',
    loadComponent: () =>
      import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(
        (m) => m.NotFoundComponent,
      ),
    title: 'Página no encontrada - Sistema Constructora',
  },
];
